import React from "react";
import {
  HiOutlineChartBar,
  HiOutlineCash,
  HiOutlineCreditCard,
  HiOutlineShoppingBag,
} from "react-icons/hi";
import { formatCurrency } from "../../transactions/utils/transactionFormatters";
import { Transaction } from "../../../domain/types/Transaction";
import { useFamilyTransactions } from "../hooks/useFamilyTransactions";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { generateChartColors } from "../utils/analyticsUtils";

/**
 * Interface defining the structure of metrics calculated from transactions
 */
interface PeriodMetrics {
  distribution: Record<string, number>;
  income: number;
  expenses: number;
  categories: Record<string, number>;
  largestTransaction: Transaction | null;
}

/**
 * Financial Snapshot Component
 *
 * Provides a comprehensive view of financial status including:
 * - Transaction type distribution (Income, Expense, Transfer, etc.)
 * - Monthly income and expenses with month-over-month trends
 * - Top spending category
 * - Largest transaction details
 *
 * Data is calculated for both current and previous months to show trends.
 */
export const FinancialSnapshot: React.FC = () => {
  const { data: transactionData } = useFamilyTransactions();
  const transactions = transactionData?.transactions || [];

  /**
   * Calculates percentage change between two values
   * @param current - Current period value
   * @param previous - Previous period value
   * @returns Percentage change, 0 if no previous data exists
   */
  const calculateTrend = (current: number, previous: number): number =>
    previous ? ((current - previous) / previous) * 100 : 0;

  /**
   * Split transactions into current and previous month metrics
   */
  const today = new Date();
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);

  /**
   * Calculate metrics for both current and previous months in a single pass
   */
  const { currentMetrics, previousMetrics } = transactions.reduce(
    (acc, t) => {
      const txDate = new Date(t.transaction_date);
      const metrics =
        txDate >= currentMonthStart
          ? acc.currentMetrics
          : txDate >= prevMonthStart
            ? acc.previousMetrics
            : acc.previousMetrics;

      // Add to distribution
      const type = t.transaction_type || "Unknown";
      metrics.distribution[type] =
        (metrics.distribution[type] || 0) + Math.abs(t.amount);

      // Track income and expenses
      if (t.transaction_type === "Income") {
        metrics.income += t.amount;
      } else if (t.transaction_type === "Expense") {
        metrics.expenses += Math.abs(t.amount);
      }

      // Track categories
      const category = t.category_description;
      metrics.categories[category] =
        (metrics.categories[category] || 0) + Math.abs(t.amount);

      // Track largest transaction
      if (
        Math.abs(t.amount) > Math.abs(metrics.largestTransaction?.amount || 0)
      ) {
        metrics.largestTransaction = t;
      }

      return acc;
    },
    {
      currentMetrics: {
        distribution: {},
        income: 0,
        expenses: 0,
        categories: {},
        largestTransaction: null,
      } as PeriodMetrics,
      previousMetrics: {
        distribution: {},
        income: 0,
        expenses: 0,
        categories: {},
        largestTransaction: null,
      } as PeriodMetrics,
    },
  );

  // Calculate trends
  const trends = {
    income: calculateTrend(currentMetrics.income, previousMetrics.income),
    expenses: calculateTrend(currentMetrics.expenses, previousMetrics.expenses),
  };

  // Prepare distribution data for pie chart
  const distributionData = Object.entries(currentMetrics.distribution).map(
    ([type, amount]) => ({
      name: type,
      value: amount,
    }),
  );

  // Get top category
  const topCategory = Object.entries(currentMetrics.categories).sort(
    ([, a], [, b]) => b - a,
  )[0] || ["No Data", 0];

  const chartColors = generateChartColors(distributionData.length);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Balance Distribution</h2>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={distributionData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              label
            >
              {distributionData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={chartColors[index]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => formatCurrency(value as number)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          title="Monthly Income"
          value={formatCurrency(currentMetrics.income)}
          trend={trends.income}
          icon={HiOutlineCash}
          color="emerald"
        />
        <MetricCard
          title="Monthly Expenses"
          value={formatCurrency(currentMetrics.expenses)}
          trend={trends.expenses}
          icon={HiOutlineChartBar}
          color="rose"
        />
        <MetricCard
          title="Top Category"
          value={topCategory[0]}
          subtext={formatCurrency(topCategory[1])}
          icon={HiOutlineShoppingBag}
          color="indigo"
        />
        <MetricCard
          title="Largest Transaction"
          value={currentMetrics.largestTransaction?.merchant_name || "No Data"}
          subtext={formatCurrency(
            currentMetrics.largestTransaction?.amount || 0,
          )}
          icon={HiOutlineCreditCard}
          color="amber"
        />
      </div>
    </div>
  );
};

/**
 * MetricCard Component Props
 */
interface MetricCardProps {
  title: string;
  value: string;
  subtext?: string;
  trend?: number;
  icon: React.ComponentType<{ className?: string }>;
  color: "emerald" | "rose" | "indigo" | "amber";
}

/**
 * MetricCard Component
 *
 * Displays a single metric with optional trend and subtext.
 * Used for showing key financial indicators.
 */
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtext,
  trend,
  icon: Icon,
  color,
}) => {
  const colorClasses = {
    emerald:
      "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400",
    rose: "bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400",
    indigo:
      "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400",
    amber:
      "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
  };

  return (
    <div className={`rounded-xl p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium opacity-80">{title}</span>
        <Icon className="w-5 h-5 opacity-80" />
      </div>
      <div className="text-xl font-bold mb-1">{value}</div>
      {trend !== undefined && (
        <div
          className={`text-sm ${trend >= 0 ? "text-emerald-600" : "text-rose-600"}`}
        >
          {trend >= 0 ? "↑" : "↓"} {Math.abs(trend).toFixed(1)}% vs last month
        </div>
      )}
      {subtext && <div className="text-sm opacity-80">{subtext}</div>}
    </div>
  );
};
